// Gradient stops for BookOneWave. Each palette has 7 stops matching the
// ElevenLabs silky-ribbon shader structure. Position 0 and 1 should be the
// same colour so the repeating gradient (uGradientRepeat=2) loops seamlessly.
//
// Add new palettes here and they're immediately available via the `palette` prop.

export type Stop = { color: string; alpha: number; position: number };
export type PaletteName =
  | 'bookone'
  | 'bookoneLight'
  | 'akiro'
  | 'akiroDark'
  | 'akiroLight'
  | 'ottavio'
  | 'ottavioLight'
  | 'mala'
  | 'clandestino'
  | 'clandestinoLight'
  | 'kayao'
  | 'kayaoLight'
  | 'ayayay'
  | 'ayayayLight'
  | 'matilda'
  | 'matildaLight'
  | 'exodo'
  | 'exodoLight'
  | 'delancey'
  | 'delanceyLight'
  | 'neutral'
  | 'blue'
  | 'cyan'
  | 'green'
  | 'magenta'
  | 'orange'
  | 'purple'
  | 'glowbal'
  | 'glowbalLight'
  | 'whiteHold'
  | 'blackGold'
  | 'blackGoldWhite'
  | 'baccala';

export const PALETTES: Record<PaletteName, Stop[]> = {
  bookone: [
    { color: '#FFE8B5', alpha: 1, position: 0.0 },   // warm cream highlight
    { color: '#F7B317', alpha: 1, position: 0.2 },   // BookOne gold
    { color: '#B8303F', alpha: 1, position: 0.3 },   // bright burgundy
    { color: '#7A1B1B', alpha: 1, position: 0.7 },   // mid burgundy
    { color: '#2B0A0A', alpha: 1, position: 0.8 },   // void
    { color: '#1A0505', alpha: 1, position: 0.9 },   // near-black
    { color: '#FFE8B5', alpha: 1, position: 1.0 },   // loop back to highlight
  ],
  // BookOne — listening/user-turn variant. Cream + gold only, no burgundy.
  // Pairs with `bookone` (the speaking variant) for BookOne-hosted widgets
  // so the wave lightens while the caller is speaking (listening-agent state)
  // and darkens to burgundy/gold while the agent responds.
  bookoneLight: [
    { color: '#FFF5D6', alpha: 1, position: 0.0 },   // pale cream highlight
    { color: '#FFE8B5', alpha: 1, position: 0.2 },   // warm cream
    { color: '#F7B317', alpha: 1, position: 0.3 },   // BookOne gold
    { color: '#C48F12', alpha: 1, position: 0.7 },   // neon-dim gold (darker core)
    { color: '#F7B317', alpha: 1, position: 0.8 },   // gold
    { color: '#FFE8B5', alpha: 1, position: 0.9 },   // warm cream
    { color: '#FFF5D6', alpha: 1, position: 1.0 },   // loop
  ],
  // Akiro Hand Roll Bar — off-white cream + two warm wood-tone accents +
  // black. Loops cream → tan → bronze → black → bronze → tan → cream for
  // a seamless banding. Matches the Akiro wordmark palette used across
  // their website + our widget styling.
  akiro: [
    { color: '#F5F0E6', alpha: 1, position: 0.0 },   // cream (off-white)
    { color: '#D6C7A5', alpha: 1, position: 0.2 },   // warm tan
    { color: '#8A6E3F', alpha: 1, position: 0.3 },   // bronze
    { color: '#1A1A1A', alpha: 1, position: 0.7 },   // black (core)
    { color: '#8A6E3F', alpha: 1, position: 0.8 },   // bronze
    { color: '#D6C7A5', alpha: 1, position: 0.9 },   // warm tan
    { color: '#F5F0E6', alpha: 1, position: 1.0 },   // cream loop
  ],
  // Akiro — talking/agent-speaking variant. Core stop is black; more weight
  // on the dark band. Used when the agent is mid-response; the ribbon reads
  // as "active" / "producing".
  akiroDark: [
    { color: '#D6C7A5', alpha: 1, position: 0.0 },   // warm tan (edges)
    { color: '#8A6E3F', alpha: 1, position: 0.2 },   // bronze
    { color: '#1A1A1A', alpha: 1, position: 0.3 },   // black
    { color: '#0A0A0A', alpha: 1, position: 0.7 },   // near-black (core)
    { color: '#1A1A1A', alpha: 1, position: 0.8 },   // black
    { color: '#8A6E3F', alpha: 1, position: 0.9 },   // bronze
    { color: '#D6C7A5', alpha: 1, position: 1.0 },   // warm tan loop
  ],
  // Ottavio — Italian flag (green + white + red). The speaking variant
  // cores on Italian red (#CE2B37) with green (#009246) edges; the
  // listening variant pairs as a paler cream + red wash. Loops cleanly
  // because stop 0 and stop 1 are the same color. Used for the Ottavio
  // widget (bilingual FR + EN, 4 Quebec locations) — reads as Italian
  // trattoria warmth rather than generic cream.
  ottavio: [
    { color: '#F5F0E6', alpha: 1, position: 0.0 },   // warm cream
    { color: '#009246', alpha: 1, position: 0.2 },   // Italian green
    { color: '#CE2B37', alpha: 1, position: 0.3 },   // Italian red bright
    { color: '#8C1C17', alpha: 1, position: 0.7 },   // deep red core
    { color: '#CE2B37', alpha: 1, position: 0.8 },   // Italian red
    { color: '#009246', alpha: 1, position: 0.9 },   // green
    { color: '#F5F0E6', alpha: 1, position: 1.0 },   // cream loop
  ],
  // Ottavio — listening/user-turn variant. Cream + red only, no green
  // core. Pairs with `ottavio` for the speaking/listening swap.
  ottavioLight: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },   // white highlight
    { color: '#F5F0E6', alpha: 1, position: 0.2 },   // warm cream
    { color: '#E8B4B8', alpha: 1, position: 0.3 },   // pale rose
    { color: '#D9322F', alpha: 1, position: 0.7 },   // Ottavio red (dimmer core)
    { color: '#E8B4B8', alpha: 1, position: 0.8 },   // pale rose
    { color: '#F5F0E6', alpha: 1, position: 0.9 },   // warm cream
    { color: '#FFFFFF', alpha: 1, position: 1.0 },   // white loop
  ],
  // MáLà Project — 4-location bilingual brand. Wave cycles through all 4
  // location-specific colors (East Village pink → Bryant Park orange →
  // Midtown East purple → Greenpoint teal) with the events-page yellow
  // anchor as the edge stop. R42.1 — dropped the dark core stop per user
  // request; the bespoke widget now sits on a yellow canvas, so a dark
  // band in the middle of the wave would read as a foreign element.
  // Reads as Mála-specific (not generic) and visually conveys the
  // multi-location identity. Loops cleanly via matching position 0 + 1
  // yellow stops. Precise hex values sampled from malaproject.com/events
  // via /browse rather than the prior approximations.
  mala: [
    { color: '#FFE71E', alpha: 1, position: 0.0 },   // events-page yellow (anchor)
    { color: '#FF5597', alpha: 1, position: 0.2 },   // East Village pink
    { color: '#FF8F5F', alpha: 1, position: 0.35 },  // Bryant Park orange
    { color: '#AB9BFF', alpha: 1, position: 0.55 },  // Midtown East purple
    { color: '#00D7B3', alpha: 1, position: 0.75 },  // Greenpoint teal
    { color: '#FF5597', alpha: 1, position: 0.9 },   // back through pink
    { color: '#FFE71E', alpha: 1, position: 1.0 },   // yellow loop
  ],
  // Akiro — listening/user-turn variant. Core stop stays cream-adjacent;
  // much less black. Reads as "waiting" / "open".
  akiroLight: [
    { color: '#F5F0E6', alpha: 1, position: 0.0 },   // cream
    { color: '#E8DDC2', alpha: 1, position: 0.2 },   // cream-tan
    { color: '#D6C7A5', alpha: 1, position: 0.3 },   // warm tan
    { color: '#8A6E3F', alpha: 1, position: 0.7 },   // bronze (core)
    { color: '#D6C7A5', alpha: 1, position: 0.8 },   // warm tan
    { color: '#E8DDC2', alpha: 1, position: 0.9 },   // cream-tan
    { color: '#F5F0E6', alpha: 1, position: 1.0 },   // cream loop
  ],
  // Clandestino Cocktail Social Club — speakeasy red on black. Mirrors the
  // venue's hero imagery: deep red (#CA1929) accents over pure black, with
  // ember/spark amber tones echoing the cocktail-flame photography on the
  // landing page. Speaking variant (this one) cores on the brand red.
  clandestino: [
    { color: '#1A0606', alpha: 1, position: 0.0 },   // near-black edge
    { color: '#7A0E18', alpha: 1, position: 0.2 },   // deep oxblood
    { color: '#CA1929', alpha: 1, position: 0.3 },   // brand red
    { color: '#0A0000', alpha: 1, position: 0.7 },   // pure black core
    { color: '#CA1929', alpha: 1, position: 0.8 },   // brand red (mirror)
    { color: '#7A0E18', alpha: 1, position: 0.9 },   // deep oxblood
    { color: '#1A0606', alpha: 1, position: 1.0 },   // near-black loop
  ],
  // Clandestino — listening/user-turn variant. Cream + pale rose with a
  // single bright-red flicker at the core. Mirrors the Akiro / Ottavio
  // pattern: speaking variant cores on the brand color, listening variant
  // cores on a soft warm light so the caller can SEE the wave shift when
  // they speak vs. when the agent speaks. Read R42.30+ staging feedback —
  // previous all-dark-red version had no visible state shift.
  clandestinoLight: [
    { color: '#F5F0E6', alpha: 1, position: 0.0 },   // cream highlight (edge)
    { color: '#EBC8C2', alpha: 1, position: 0.2 },   // pale rose
    { color: '#D9322F', alpha: 1, position: 0.3 },   // brighter clandestino red
    { color: '#CA1929', alpha: 1, position: 0.7 },   // brand red core (dimmer)
    { color: '#D9322F', alpha: 1, position: 0.8 },   // brighter red (mirror)
    { color: '#EBC8C2', alpha: 1, position: 0.9 },   // pale rose
    { color: '#F5F0E6', alpha: 1, position: 1.0 },   // cream loop
  ],
  // Kayao — Chifa speakeasy palette. Deep teal canvas with coral fish color
  // at the core, accented by cream highlights at the edges. Speaking
  // variant cores on the brand coral so the agent reads as "warm + active".
  kayao: [
    { color: '#013334', alpha: 1, position: 0.0 },   // deep teal edge
    { color: '#024041', alpha: 1, position: 0.2 },   // teal mid
    { color: '#FD6632', alpha: 1, position: 0.3 },   // coral (brand)
    { color: '#E55524', alpha: 1, position: 0.7 },   // coral darker (core)
    { color: '#FD6632', alpha: 1, position: 0.8 },   // coral mirror
    { color: '#024041', alpha: 1, position: 0.9 },   // teal mid
    { color: '#013334', alpha: 1, position: 1.0 },   // deep teal loop
  ],
  // Kayao — listening/user-turn variant. Cream + pale-coral highlights so
  // the wave reads as "I'm listening, hold the moment" when caller speaks.
  // Mirrors the Akiro / Clandestino pair pattern (light ↔ branded).
  kayaoLight: [
    { color: '#E8E4D8', alpha: 1, position: 0.0 },   // cream highlight (edge)
    { color: '#F4D6C5', alpha: 1, position: 0.2 },   // pale coral
    { color: '#FD9670', alpha: 1, position: 0.3 },   // bright coral
    { color: '#FD6632', alpha: 1, position: 0.7 },   // brand coral core (dimmer)
    { color: '#FD9670', alpha: 1, position: 0.8 },   // bright coral mirror
    { color: '#F4D6C5', alpha: 1, position: 0.9 },   // pale coral
    { color: '#E8E4D8', alpha: 1, position: 1.0 },   // cream loop
  ],
  // Ayayay — pink + green + coral key art palette. Sampled from the
  // ayayaymex.com illustrated scene (cocktail + lime + chili). Speaking
  // variant cores on the deep forest green (#0F5A4D) — the signature
  // drop/lime color — with coral (#D33C2A) and cream (#FAEDC7) as mid
  // stops, framed by the salmon pink (#F5A8A0) canvas. Reads as
  // distinctly Ayayay, not Kayao-adjacent.
  ayayay: [
    { color: '#F5A8A0', alpha: 1, position: 0.0 },   // salmon pink edge (canvas)
    { color: '#FAEDC7', alpha: 1, position: 0.2 },   // cream
    { color: '#D33C2A', alpha: 1, position: 0.3 },   // bright coral
    { color: '#0F5A4D', alpha: 1, position: 0.7 },   // deep forest green core
    { color: '#D33C2A', alpha: 1, position: 0.8 },   // bright coral mirror
    { color: '#FAEDC7', alpha: 1, position: 0.9 },   // cream
    { color: '#F5A8A0', alpha: 1, position: 1.0 },   // salmon pink loop
  ],
  // Ayayay — listening/user-turn variant. Cores on cream so the wave
  // visibly lightens when the caller speaks. Same pink-cream-coral hues,
  // just inverted weight: cream is the anchor, coral is a flicker, no
  // green at all. Mirrors Akiro / Clandestino / Kayao light pattern.
  ayayayLight: [
    { color: '#F5A8A0', alpha: 1, position: 0.0 },   // salmon pink edge
    { color: '#F8C9C2', alpha: 1, position: 0.2 },   // pale pink
    { color: '#FAEDC7', alpha: 1, position: 0.3 },   // cream
    { color: '#D33C2A', alpha: 1, position: 0.7 },   // bright coral core (dimmer)
    { color: '#FAEDC7', alpha: 1, position: 0.8 },   // cream mirror
    { color: '#F8C9C2', alpha: 1, position: 0.9 },   // pale pink
    { color: '#F5A8A0', alpha: 1, position: 1.0 },   // salmon pink loop
  ],
  // Matilda — minimal cream-and-black palette. Mirrors the venue's only-two-
  // colors brand identity. Speaking variant cores on pure black with cream
  // edges + a single warm-tan midpoint (sampled from the wood banquettes
  // in the venue's hero image); listening variant cores on the warm tan
  // for the "I'm holding the moment" state shift. No competing color —
  // the wave reads as silhouette, like the M emblem itself.
  matilda: [
    { color: '#F9F2E6', alpha: 1, position: 0.0 },   // warm cream edge
    { color: '#E8DBC2', alpha: 1, position: 0.2 },   // cream-tan
    { color: '#A88B5A', alpha: 1, position: 0.3 },   // wood tan (banquette tone)
    { color: '#000000', alpha: 1, position: 0.7 },   // pure black core
    { color: '#A88B5A', alpha: 1, position: 0.8 },   // wood tan mirror
    { color: '#E8DBC2', alpha: 1, position: 0.9 },   // cream-tan
    { color: '#F9F2E6', alpha: 1, position: 1.0 },   // warm cream loop
  ],
  // Matilda — listening/user-turn variant. Cores on the wood tan so the
  // wave visibly lightens while the caller speaks vs the saturated black
  // core when the agent responds. Same Akiro / Ottavio / Kayao / Ayayay
  // pair pattern (light ↔ branded).
  matildaLight: [
    { color: '#F9F2E6', alpha: 1, position: 0.0 },   // warm cream edge
    { color: '#F2EBDA', alpha: 1, position: 0.2 },   // cream
    { color: '#E8DBC2', alpha: 1, position: 0.3 },   // cream-tan
    { color: '#A88B5A', alpha: 1, position: 0.7 },   // wood tan core (dimmer)
    { color: '#E8DBC2', alpha: 1, position: 0.8 },   // cream-tan mirror
    { color: '#F2EBDA', alpha: 1, position: 0.9 },   // cream
    { color: '#F9F2E6', alpha: 1, position: 1.0 },   // warm cream loop
  ],
  // Exodo — pure black + white palette. Mirrors the parent group's
  // minimal two-color identity (no competing hue). Speaking variant cores
  // on pure white with black edges + soft grey midpoints. The "Joining
  // Movement" CTA energy on exodohospitality.com is high-contrast B&W;
  // the wave reads as silhouette.
  exodo: [
    { color: '#000000', alpha: 1, position: 0.0 },   // pure black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#666666', alpha: 1, position: 0.3 },   // mid grey
    { color: '#FFFFFF', alpha: 1, position: 0.7 },   // pure white core
    { color: '#666666', alpha: 1, position: 0.8 },   // mid grey mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // pure black loop
  ],
  // Exodo — listening/user-turn variant. Cores on mid grey so the wave
  // visibly dims while the caller speaks vs the bright-white core when
  // the agent responds. Same Akiro / Clandestino / Kayao pair pattern.
  exodoLight: [
    { color: '#000000', alpha: 1, position: 0.0 },   // pure black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#404040', alpha: 1, position: 0.3 },   // dark grey
    { color: '#808080', alpha: 1, position: 0.7 },   // mid grey core (dimmer)
    { color: '#404040', alpha: 1, position: 0.8 },   // dark grey mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // pure black loop
  ],
  // The Delancey NYC — black canvas + cream (#F2EBD8) accent palette.
  // Speaking variant cores on a warm gold/amber (#C9A55C — the lit-bar
  // amber tone from the venue's hero photography) framed by cream edges
  // and the black canvas. NYC nightclub energy: warm + grown-up.
  delancey: [
    { color: '#000000', alpha: 1, position: 0.0 },   // black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#8A6E3F', alpha: 1, position: 0.3 },   // bronze
    { color: '#C9A55C', alpha: 1, position: 0.7 },   // warm gold/amber core
    { color: '#8A6E3F', alpha: 1, position: 0.8 },   // bronze mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // black loop
  ],
  // Delancey — listening/user-turn variant. Cream-cored so the wave
  // visibly lightens when the caller speaks vs the warm gold when the
  // agent responds. Same Akiro / Ottavio listening↔speaking pattern.
  delanceyLight: [
    { color: '#000000', alpha: 1, position: 0.0 },   // black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#8A6E3F', alpha: 1, position: 0.3 },   // bronze
    { color: '#F2EBD8', alpha: 1, position: 0.7 },   // cream core (lighter)
    { color: '#8A6E3F', alpha: 1, position: 0.8 },   // bronze mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // black loop
  ],
  neutral: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#E5E5E5', alpha: 1, position: 0.2 },
    { color: '#949494', alpha: 1, position: 0.3 },
    { color: '#DCDCDC', alpha: 1, position: 0.7 },
    { color: '#525252', alpha: 1, position: 0.8 },
    { color: '#292929', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  blue: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#D3F4FA', alpha: 1, position: 0.2 },
    { color: '#2857FF', alpha: 1, position: 0.3 },
    { color: '#A1BAEC', alpha: 1, position: 0.7 },
    { color: '#4056CE', alpha: 1, position: 0.8 },
    { color: '#353B73', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  cyan: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#E0F1FE', alpha: 1, position: 0.2 },
    { color: '#1AC6F6', alpha: 1, position: 0.3 },
    { color: '#74D9EC', alpha: 1, position: 0.7 },
    { color: '#1783A3', alpha: 1, position: 0.8 },
    { color: '#1D495C', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  green: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#9EF0DE', alpha: 1, position: 0.2 },
    { color: '#49F28D', alpha: 1, position: 0.3 },
    { color: '#2DD28D', alpha: 1, position: 0.7 },
    { color: '#047851', alpha: 1, position: 0.8 },
    { color: '#064E37', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  magenta: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#EEDBF9', alpha: 1, position: 0.2 },
    { color: '#EC5CDB', alpha: 1, position: 0.3 },
    { color: '#F0B2EA', alpha: 1, position: 0.7 },
    { color: '#973086', alpha: 1, position: 0.8 },
    { color: '#642656', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  orange: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#FAF9C7', alpha: 1, position: 0.2 },
    { color: '#FF6E1D', alpha: 1, position: 0.3 },
    { color: '#F9B778', alpha: 1, position: 0.7 },
    { color: '#BD3F11', alpha: 1, position: 0.8 },
    { color: '#792C15', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  purple: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },
    { color: '#FBE4FB', alpha: 1, position: 0.2 },
    { color: '#B951FB', alpha: 1, position: 0.3 },
    { color: '#E1BEF4', alpha: 1, position: 0.7 },
    { color: '#6E1DAD', alpha: 1, position: 0.8 },
    { color: '#3A0E5B', alpha: 1, position: 0.9 },
    { color: '#FFFFFF', alpha: 1, position: 1.0 },
  ],
  // Glowbal Restaurant Group — magenta-purple #8B2A8C (the group's BOOKING
  // REQUEST button color) on a near-black grey core, mirroring the venue's
  // grey-and-purple brand. `glowbal` = agent-speaking (darker core), pairs with
  // `glowbalLight` = listening (lighter, brighter purple).
  glowbal: [
    { color: '#F2D9F2', alpha: 1, position: 0.0 },   // pale orchid edge
    { color: '#C24FC3', alpha: 1, position: 0.2 },   // bright magenta-purple
    { color: '#8B2A8C', alpha: 1, position: 0.3 },   // brand purple
    { color: '#2E2C2E', alpha: 1, position: 0.7 },   // brand grey core
    { color: '#8B2A8C', alpha: 1, position: 0.8 },   // brand purple
    { color: '#C24FC3', alpha: 1, position: 0.9 },   // bright magenta-purple
    { color: '#F2D9F2', alpha: 1, position: 1.0 },   // orchid loop
  ],
  glowbalLight: [
    { color: '#FBEFFB', alpha: 1, position: 0.0 },   // near-white orchid
    { color: '#E0A9E1', alpha: 1, position: 0.2 },   // soft orchid
    { color: '#C24FC3', alpha: 1, position: 0.3 },   // bright magenta-purple
    { color: '#8B2A8C', alpha: 1, position: 0.7 },   // brand purple core
    { color: '#C24FC3', alpha: 1, position: 0.8 },   // bright magenta-purple
    { color: '#E0A9E1', alpha: 1, position: 0.9 },   // soft orchid
    { color: '#FBEFFB', alpha: 1, position: 1.0 },   // loop
  ],
  // Shared WHITE listening/hold palette for the Glowbal group. When the
  // CUSTOMER speaks (agent is listening), every Glowbal venue's ribbon flips
  // to this near-pure-white gradient — a clean, unmistakable "I'm listening"
  // state vs. the brand-colored agent-speaking state. White edges match the
  // generic ElevenLabs palettes (cyan/blue/green/orange) so the cross-fade
  // only moves the CORE from brand-color → white (no edge motion). The faint
  // cool-grey core (#DDE0E6) gives the white ribbon visible shape on a dark
  // panel instead of washing out to a flat invisible white. Reads as white on
  // every dark Glowbal stage (grey/teal/slate/near-black/forest-green).
  whiteHold: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },   // pure white edge
    { color: '#FFFFFF', alpha: 1, position: 0.2 },   // white
    { color: '#F4F5F7', alpha: 1, position: 0.3 },   // near-white
    { color: '#DDE0E6', alpha: 1, position: 0.7 },   // faint cool-grey core (ribbon shape)
    { color: '#F4F5F7', alpha: 1, position: 0.8 },   // near-white
    { color: '#FFFFFF', alpha: 1, position: 0.9 },   // white
    { color: '#FFFFFF', alpha: 1, position: 1.0 },   // white loop
  ],
  // Black + Gold — Glowbal steakhouse speaking palette (The Roof + Black + Blue).
  // Gold/amber core on a pure-black canvas, byte-identical in spirit to the
  // Delancey NYC palette but a SEPARATE key so the Glowbal steakhouses can flip
  // to white on listening (blackGoldWhite) WITHOUT changing The Delancey NYC's
  // cream-flip behavior. Decouples shared-palette blast radius.
  blackGold: [
    { color: '#000000', alpha: 1, position: 0.0 },   // black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#8A6E3F', alpha: 1, position: 0.3 },   // bronze
    { color: '#C9A55C', alpha: 1, position: 0.7 },   // warm gold/amber core
    { color: '#8A6E3F', alpha: 1, position: 0.8 },   // bronze mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // black loop
  ],
  // Black + Gold — listening/hold variant. Edges stay PURE BLACK (matching the
  // near-black steakhouse panel, so they vanish into it) and ONLY the core
  // flips gold → white. The cross-fade is therefore edge-stable: the ribbon's
  // glowing center swaps from warm gold (agent speaking) to clean white
  // (customer speaking) with no jarring black↔white edge swing.
  blackGoldWhite: [
    { color: '#000000', alpha: 1, position: 0.0 },   // black edge
    { color: '#1C1C1C', alpha: 1, position: 0.2 },   // near-black
    { color: '#8F8F94', alpha: 1, position: 0.3 },   // grey transition
    { color: '#FFFFFF', alpha: 1, position: 0.7 },   // white core
    { color: '#8F8F94', alpha: 1, position: 0.8 },   // grey transition mirror
    { color: '#1C1C1C', alpha: 1, position: 0.9 },   // near-black
    { color: '#000000', alpha: 1, position: 1.0 },   // black loop
  ],
  // Baccalà — white + teal. Clean white edges, a sage→teal core that reads as
  // the brand's coastal seafood-and-wine palette. White on the loop so the
  // ribbon dissolves cleanly into the deep-teal widget panel.
  baccala: [
    { color: '#FFFFFF', alpha: 1, position: 0.0 },   // white edge
    { color: '#D8E5E1', alpha: 1, position: 0.2 },   // pale sage
    { color: '#7FB3A8', alpha: 1, position: 0.3 },   // sage-teal
    { color: '#46C5B0', alpha: 1, position: 0.7 },   // bright teal core
    { color: '#2F8276', alpha: 1, position: 0.8 },   // deep teal
    { color: '#1E4E47', alpha: 1, position: 0.9 },   // dark teal
    { color: '#FFFFFF', alpha: 1, position: 1.0 },   // white loop
  ],
};
